import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, ExternalLink, Award } from "lucide-react";
import { Reveal, CountUp, Eyebrow } from "../components/common";
import { STATS, OEM_PARTNERS, WHY_CHOOSE, TOWNS, IMAGES } from "../mock";
import { useSiteData } from "../context/SiteData";
import { findImage, resolveImage } from "../lib/api";

export default function Home() {
  const { data, loaded } = useSiteData();
  const img = (id, fallback) => resolveImage(loaded ? findImage(data.images, id)?.path : null, fallback);
  const homeNews = loaded ? (data.news.find((n) => n.showOnHome && n.pinned) || data.news.find((n) => n.showOnHome)) : null;

  return (
    <>
      {/* HERO */}
      <section className="hero-wash relative overflow-hidden">
        <span className="vert-text absolute left-4 top-1/2 -translate-y-1/2 hidden md:block">
          Ambala · Kurukshetra · Karnal · Yamuna Nagar
        </span>
        <span className="vert-text absolute right-4 top-1/2 -translate-y-1/2 hidden md:block" style={{ writingMode: "vertical-lr" }}>
          Authorised Distributor — Est. 1996
        </span>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-36 pb-24 md:pt-44 md:pb-32">
          <div className="max-w-3xl">
            <Reveal><Eyebrow>Since 1996 · Northern Haryana</Eyebrow></Reveal>
            <Reveal delay={90}>
              <h1 className="font-head font-extrabold text-[var(--rs-blue-bright)] text-5xl md:text-7xl leading-[1.02] mt-6">
                Authorised Parts Distributors
              </h1>
            </Reveal>
            <Reveal delay={180}>
              <p className="text-[var(--rs-muted)] text-lg leading-relaxed mt-7 max-w-2xl">
                Rajiv Syndicate Group has supplied genuine automobile parts across northern Haryana since 1996 — today the authorised distributor for Maruti Suzuki Genuine Parts, Amaron Batteries and Hero MotoCorp Genuine Parts, running warehouse inventory management across Ambala, Kurukshetra, Karnal, Yamuna Nagar, Kaithal, Panipat and Sonipat.
              </p>
            </Reveal>
            <Reveal delay={260}>
              <div className="flex flex-wrap gap-4 mt-9">
                <Link to="/services" className="btn-primary rounded-lg px-7 py-3.5 text-sm font-semibold inline-flex items-center gap-2">
                  View Our Services <ArrowRight size={17} />
                </Link>
                <Link to="/locations" className="btn-outline rounded-lg px-7 py-3.5 text-sm font-semibold">
                  Find a Retail Outlet
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-[var(--rs-bg)] border-y border-[var(--rs-border)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-[var(--rs-border)]">
            {STATS.map((s, i) => (
              <Reveal key={s.label} delay={i * 90} className="px-4 md:px-8 py-10">
                <div className="font-head font-extrabold text-4xl md:text-5xl text-[var(--rs-blue)]">
                  <CountUp value={s.value} isYear={s.isYear} />
                </div>
                <p className="text-[var(--rs-muted)] text-sm mt-3 leading-snug">{s.label}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* OEM PARTNERSHIPS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <Reveal><Eyebrow>Authorised distributorships</Eyebrow></Reveal>
        <Reveal delay={80}>
          <h2 className="font-head font-extrabold text-3xl md:text-5xl leading-tight mt-4 max-w-2xl">
            Three OEM partnerships, one warehouse discipline
          </h2>
        </Reveal>
        <Reveal delay={140}>
          <p className="text-[var(--rs-muted)] text-lg mt-5 max-w-2xl">
            Each distributorship runs on the same principle — right part, right place, right time — backed by dedicated warehouse inventory management.
          </p>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6 mt-14">
          {OEM_PARTNERS.map((p, i) => (
            <Reveal key={p.name} delay={i * 110}>
              <div className="card-hover h-full bg-white border border-[var(--rs-border)] rounded-2xl p-7">
                <span className="font-mono-label text-xs inline-block border border-[var(--rs-border)] rounded-md px-3 py-1 text-[var(--rs-muted)]">{p.since}</span>
                <h3 className="font-head font-bold text-2xl mt-5">{p.name}</h3>
                <p className="text-[var(--rs-muted)] text-sm leading-relaxed mt-3">{p.desc}</p>
                <ul className="mt-6 space-y-2.5">
                  {p.items.map((it) => (
                    <li key={it} className="flex items-center gap-2.5 text-sm text-[var(--rs-ink)]">
                      <span className="text-[var(--rs-red)] font-bold">–</span>
                      {it}
                      <ExternalLink size={13} className="text-[var(--rs-muted)]" />
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="bg-[var(--rs-bg)] border-y border-[var(--rs-border)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img src={img("home-hero-warehouse", IMAGES.warehouseWorker)} alt="Warehouse operations" className="w-full h-[380px] object-cover" />
            </div>
            <div className="absolute -bottom-8 -right-4 md:right-8 w-48 md:w-60 rounded-xl overflow-hidden shadow-2xl border-4 border-white hidden sm:block">
              <img src={img("home-hero-aisle", IMAGES.warehouseAisle)} alt="Genuine parts stock" className="w-full h-40 object-cover" />
            </div>
          </Reveal>
          <Reveal delay={120}>
            <Eyebrow>Why Rajiv Syndicate</Eyebrow>
            <h2 className="font-head font-extrabold text-3xl md:text-5xl leading-tight mt-4">Why Choose Rajiv Syndicate?</h2>
            <p className="text-[var(--rs-muted)] text-lg mt-5">
              Genuine parts distribution and warehouse discipline across Northern Haryana is our only business — not a side line.
            </p>
            <ul className="mt-8 space-y-5">
              {WHY_CHOOSE.map((w) => (
                <li key={w} className="flex gap-3.5">
                  <span className="shrink-0 mt-0.5 w-6 h-6 rounded-full bg-[var(--rs-blue)] text-white flex items-center justify-center"><Check size={14} strokeWidth={3} /></span>
                  <span className="text-[var(--rs-ink)] leading-relaxed">{w}</span>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-4 mt-9">
              <Link to="/services" className="btn-primary rounded-lg px-6 py-3 text-sm font-semibold inline-flex items-center gap-2">Discover Our Services <ArrowRight size={16} /></Link>
              <Link to="/contact" className="btn-outline rounded-lg px-6 py-3 text-sm font-semibold inline-flex items-center gap-2">Contact Us <ArrowRight size={16} /></Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAMILY */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <Eyebrow>Thirty years, one family</Eyebrow>
          <h2 className="font-head font-extrabold text-3xl md:text-[2.7rem] leading-tight mt-4">
            Family-run since 1996, built on OEM trust rather than a consumer brand of its own
          </h2>
          <p className="text-[var(--rs-muted)] text-lg mt-6">
            Rajiv Syndicate Group is led by Directors Sanjay Jain and Rajiv Jain, and now operates as four firms — Rajiv Syndicate AutoZone Pvt Ltd., Rajiv Syndicate, Rajiv Motors and Ambala Syndicate. The base is Kuldeep Nagar, GT Road, Ambala Cantt.
          </p>
          <div className="flex flex-wrap gap-4 mt-9">
            <Link to="/history" className="btn-outline rounded-lg px-6 py-3 text-sm font-semibold">Read Our History</Link>
            <Link to="/about" className="btn-outline rounded-lg px-6 py-3 text-sm font-semibold">About the Group</Link>
          </div>
        </Reveal>
        <Reveal delay={120}>
          <div className="rounded-2xl overflow-hidden shadow-2xl">
            <img src={img("home-about-teaser", IMAGES.workshop)} alt="Ambala Cantt warehouse floor" className="w-full h-[420px] object-cover" />
          </div>
        </Reveal>
      </section>

      {/* TERRITORY */}
      <section className="bg-[var(--rs-bg)] border-y border-[var(--rs-border)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <Reveal><Eyebrow>Territory coverage</Eyebrow></Reveal>
          <Reveal delay={80}><h2 className="font-head font-extrabold text-3xl md:text-5xl mt-4">Present across Northern Haryana</h2></Reveal>
          <Reveal delay={140}><p className="text-[var(--rs-muted)] text-lg mt-5 max-w-2xl">Thirteen retail outlets and dedicated warehouses across eleven towns — from Ambala Cantt to Sonipat.</p></Reveal>
          <div className="flex flex-wrap gap-3 mt-10">
            {TOWNS.map((t, i) => (
              <Reveal key={t} delay={i * 40}>
                <span className="chip inline-block bg-white border border-[var(--rs-border)] rounded-lg px-5 py-3 text-sm font-medium cursor-default">{t}</span>
              </Reveal>
            ))}
          </div>
          <Reveal delay={120}>
            <Link to="/locations" className="btn-outline rounded-lg px-6 py-3 text-sm font-semibold inline-block mt-10">View Full Directory</Link>
          </Reveal>
        </div>
      </section>

      {/* AWARD */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <Reveal>
          <div className="bg-gradient-to-br from-[#eef3fe] to-[#f7f9fd] border border-[var(--rs-border)] rounded-2xl p-8 md:p-12 flex flex-col md:flex-row gap-8">
            <div className="shrink-0">
              <div className="w-16 h-16 rounded-xl bg-[var(--rs-blue)] text-white flex items-center justify-center"><Award size={30} /></div>
              <span className="font-mono-label text-2xl font-bold text-[var(--rs-blue)] block mt-3">{homeNews?.badge || "'23"}</span>
            </div>
            <div>
              <h3 className="font-head font-bold text-2xl">{homeNews ? homeNews.title : "Hero MotoCorp — Best Distributor Award, 2022–23"}</h3>
              <p className="text-[var(--rs-muted)] mt-3 max-w-3xl leading-relaxed">
                {homeNews ? homeNews.body : "Presented to Director Sanjay Jain by Mr. Akhilesh Vijay, National Head Parts, during the National Parts Distributors Conference held in Malaysia — recognising Rajiv Syndicate's performance as Hero MotoCorp Super Stockiest across northern Haryana."}
              </p>
              <Link to="/newspaper" className="btn-primary rounded-lg px-6 py-3 text-sm font-semibold inline-flex items-center gap-2 mt-6">See Our Recognition <ArrowRight size={16} /></Link>
            </div>
          </div>
        </Reveal>
      </section>

      {/* CTA */}
      <section className="bg-[var(--rs-bg)] border-t border-[var(--rs-border)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 text-center">
          <Reveal><h2 className="font-head font-extrabold text-3xl md:text-5xl">Looking for genuine parts in your territory?</h2></Reveal>
          <Reveal delay={120}>
            <div className="flex flex-wrap gap-4 justify-center mt-9">
              <Link to="/contact" className="btn-primary rounded-lg px-7 py-3.5 text-sm font-semibold">Contact Us</Link>
              <Link to="/locations" className="btn-outline rounded-lg px-7 py-3.5 text-sm font-semibold">Find a Retail Outlet</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
